import React, { useState, useEffect, useCallback } from "react"
import { getJSON } from "../../../../shared_component/utils/fetch"
import sortBy from "lodash/sortBy"
import uniqBy from "lodash/uniqBy"
import { connect } from "react-redux"
import { updateVehicleInfo } from "../../../../v1/actions/vehicles"
import { Dropdown, Button, Loader, Card, Image, Header } from "semantic-ui-react"
import { withTranslation } from "react-i18next"
import Notifications from "../../../../v1/components/Notifications"
import InfoIcon from "../../../../images/info_icon_vector_orange.svg"
import store from "../../../../shared_component/utils/configureStore"
import { addVehicleYearRange } from "../../../../v1/helpers/vehicleHelpers"
import { routerPaths } from "../../../../v1/constants/paths"

const BY_YEAR_URL = "/api/v4/vehicles/years/:year/makes"
const BY_YEAR_AND_MAKE_URL = "/api/v4/vehicles/years/:year/makes/:make"
const BY_MODEL_AND_SUB_MODEL_URL =
  "/api/v4/vehicles/years/:year/makes/:make/models/:model/submodels/:submodel"
import VehicleInfo from "./VehicleInfo"

const AddVehicleManual = (props) => {
  const {
    user,
    t,
    selectedVehicle,
    updateVehicleInfo,
    history,
    asModal,
    handleSearchAgain,
    closeModalHandler
  } = props
  const [years] = useState(
    addVehicleYearRange().map((n) => ({
      key: String(n),
      text: String(n),
      value: String(n)
    }))
  )
  const [makes, setMakes] = useState([])
  const [models, setModels] = useState([])
  const [subModels, setSubModels] = useState([])
  const [engines, setEngines] = useState([])
  const [year, setYear] = useState("")
  const [make, setMake] = useState("")
  const [model, setModel] = useState("")
  const [subModel, setSubModel] = useState("")
  const [engine, setEngine] = useState("")
  const [loadingMakes, setLoadingMakes] = useState(false)
  const [loadingModels, setLoadingModels] = useState(false)
  const [loadingSubModels, setLoadingSubModels] = useState(false)
  const [loadingEngines, setLoadingEngines] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showVehicle, setShowVehicle] = useState(false)

  useEffect(() => {
    if (selectedVehicle) {
      if (selectedVehicle.year) {
        setYear(String(selectedVehicle.year))
      }
      if (selectedVehicle.make) {
        setMake(selectedVehicle.make)
      }
      if (selectedVehicle.model) {
        setModel(selectedVehicle.model)
      }
      if (selectedVehicle.sub_model) {
        setSubModel(selectedVehicle.sub_model)
      }
    }
  }, [selectedVehicle])

  const fetchMakes = useCallback(async () => {
    if (!year) return
    setLoadingMakes(true)
    let response = await getJSON(BY_YEAR_URL.replace(":year", year))
    setLoadingMakes(false)
    if (response.error) {
      store.dispatch(
        Notifications.error({
          title: t("errorLbl"),
          message: response.error.toString(),
          position: "tr",
          autoDismiss: 5,
          action: { label: "Dismiss" }
        })
      )
    } else if (response.result) {
      let makesArr = response.result.map(({ make_id, make }) => {
        return { key: String(make_id), text: make, value: String(make) }
      })
      makesArr = sortBy(makesArr, ({ text }) => text.toLowerCase())
      setMakes(makesArr)
      setModels([])
      setSubModels([])
      setEngines([])
      if (selectedVehicle?.year != year) {
        setMake("")
        setModel("")
        setSubModel("")
      }
    }
  }, [year, t, selectedVehicle?.make])

  const fetchModels = useCallback(async () => {
    if (!year || !make) return
    let makeObj = makes.find(({ value }) => value === make) || {}
    let make_id = makeObj.key
    if (!make_id) return
    setLoadingModels(true)
    let response = await getJSON(
      BY_YEAR_AND_MAKE_URL.replace(":year", year).replace(":make", make_id)
    )
    setLoadingModels(false)
    if (response.error) {
      store.dispatch(
        Notifications.error({
          title: t("errorLbl"),
          message: response.error.toString(),
          position: "tr",
          autoDismiss: 5,
          action: { label: "Dismiss" }
        })
      )
    } else if (response.result) {
      let modelsArr = response.result.map((vehicle) => ({
        key: String(vehicle.model_id),
        text: vehicle.model,
        value: String(vehicle.model)
      }))
      modelsArr = uniqBy(modelsArr, "key")
      modelsArr = sortBy(modelsArr, ({ text }) => text.toLowerCase())
      setModels(modelsArr)
      const subModelsArr = response.result.map((vehicle) => ({
        key: String(vehicle.sub_model_id),
        text: vehicle.sub_model,
        value: String(vehicle.sub_model),
        model: vehicle.model
      }))
      setSubModels(sortBy(uniqBy(subModelsArr, "key"), ({ text }) => text.toLowerCase()))
      if (selectedVehicle?.make != make) {
        setModel("")
        setSubModel("")
        setEngines([])
      }
    }
  }, [year, make, makes, t, selectedVehicle?.model])

  const fetchEngines = useCallback(async () => {
    if (!year || !make || !model || !subModel) return

    const makeObj = makes.find(({ value }) => value === make) || {}
    const make_id = makeObj.key
    if (!make_id) return

    const subModelObj = subModels.find((a) => a.value === subModel && a.model === model) || {}
    const sub_model_id = subModelObj.key
    if (!sub_model_id) return

    const modelObj = models.find(({ value }) => value === model) || {}
    const model_id = modelObj.key
    if (!model_id) return

    setLoadingEngines(true)
    const response = await getJSON(
      BY_MODEL_AND_SUB_MODEL_URL.replace(":year", year)
        .replace(":make", make_id)
        .replace(":model", model_id)
        .replace(":submodel", sub_model_id)
    )
    setLoadingEngines(false)

    if (response.error) {
      store.dispatch(
        Notifications.error({
          title: t("errorLbl"),
          message: response.error.toString(),
          position: "tr",
          autoDismiss: 5,
          action: { label: "Dismiss" }
        })
      )
    } else if (response.result) {
      let enginesArr = response.result.map(({ id, description }) => ({
        key: String(id),
        text: description,
        value: description
      }))
      setEngines(sortBy(enginesArr, ({ text }) => text.toLowerCase()))
    }
  }, [year, make, model, subModel, makes, models, subModels, t])

  const onYearChange = (value) => {
    setYear(value)
  }
  const onMakeChange = (value) => {
    setMake(value)
  }
  const onModelChange = (value) => {
    setModel(value)
  }
  const onSubModelChange = (value) => {
    setSubModel(value)
  }
  const onEngineChange = (value) => {
    setEngine(value)
  }

  const onClickNext = async () => {
    setIsLoading(true)
    let vehicle = {}
    vehicle.id = selectedVehicle.id
    vehicle.year = year
    vehicle.make = make
    let makeObj = makes.find(({ value }) => value === make) || {}
    let modelObj = models.find(({ value }) => value === model) || {}
    let engineObj = engines.find(({ value }) => value === engine) || {}
    vehicle.motorMakeID = makeObj.key
    vehicle.motorModelID = modelObj.key
    vehicle.model = modelObj.text
    vehicle.sub_model = subModel
    vehicle.engine = engine
    vehicle.motorEngineID = engineObj.key
    try {
      let response = await updateVehicleInfo(vehicle)
      if (response().result) {
        if (asModal) {
          setShowVehicle(true)
        } else {
          history.push({
            pathname: routerPaths.confirmVehicle,
            state: { from: routerPaths.addVehicleManual }
          })
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (year) fetchMakes()
  }, [year, fetchMakes])

  useEffect(() => {
    if (make) fetchModels()
  }, [make, fetchModels])

  useEffect(() => {
    if (subModel) fetchEngines()
  }, [subModel, fetchEngines])

  const formValid = year && make && model

  return showVehicle ? (
    <VehicleInfo
      handleSearchAgain={handleSearchAgain}
      asModal={asModal}
      closeModalHandler={closeModalHandler}
    />
  ) : (
    <div className="manual-vehicle-card">
      <div className="manual-vehicle-content">
        <Header as="h3">{t("We couldn’t find your vehicle!")}</Header>
        <Card fluid className="manual-vehicle-info">
          <Image src={InfoIcon} />
          <span>
            <b>{t("Your VIN looks correct, but we couldn’t find a vehicle match.")}</b>{" "}
            {t(" Add your vehicle details to continue.")}
          </span>
        </Card>
        <label>
          {t("Year")} <span style={{ color: "red" }}>*</span>
        </label>
        <Dropdown
          fluid
          selection
          required
          placeholder={t("yearPlaceholder")}
          options={years}
          value={year}
          onChange={(_, data) => onYearChange(data.value)}
          loading={false}
        />
        <label>
          {t("Make")} <span style={{ color: "red" }}>*</span>
        </label>
        <Dropdown
          fluid
          selection
          required
          placeholder={t("makePlaceholder")}
          options={makes}
          value={make}
          disabled={!year}
          onChange={(_, data) => onMakeChange(data.value)}
          loading={loadingMakes}
        />
        <label>
          {t("Model")} <span style={{ color: "red" }}>*</span>
        </label>
        <Dropdown
          fluid
          selection
          required
          placeholder={t("modelPlaceholder")}
          options={models}
          value={model}
          disabled={!make}
          onChange={(_, data) => onModelChange(data.value)}
          loading={loadingModels}
        />
        <label>{t("Sub-model (optional)")}</label>
        <Dropdown
          fluid
          selection
          placeholder={t("subModelPlaceholder")}
          options={subModels.filter((sm) => sm.model === model)}
          disabled={!model}
          value={subModel}
          onChange={(_, data) => onSubModelChange(data.value)}
          loading={loadingSubModels}
        />
        <label>{t("Engine (optional)")}</label>
        <Dropdown
          fluid
          selection
          placeholder={t("enginePlaceholder")}
          options={engines}
          disabled={!subModel}
          value={engine}
          onChange={(_, data) => onEngineChange(data.value)}
          loading={loadingEngines}
        />
      </div>
      <Button
        fluid
        disabled={isLoading || !formValid}
        loading={isLoading}
        onClick={onClickNext}
        className="primary-button"
      >
        {isLoading ? <Loader active inline size="small" /> : t("addMyVehicle")}
      </Button>
    </div>
  )
}

function mapStateToProps(state, ownProps) {
  let user = state.user || {}
  let selectedVehicle = state.selectedVehicle.selectedVehicle || {}

  return {
    user: user,
    selectedVehicle: selectedVehicle
  }
}

export default connect(mapStateToProps, { updateVehicleInfo })(
  withTranslation("vehicleDetails")(AddVehicleManual)
)
